import { QuoterV1, QuoterV2, encodePath } from '@trinity/pharaoh-common';
import { Contract, formatUnits } from 'ethers';
export class QuoteService {
    constructor(runner, quoterV2Address, quoterV1Address, tokenService) {
        this.runner = runner;
        this.quoterV2 = new Contract(quoterV2Address, QuoterV2, runner);
        this.quoterV1 = new Contract(quoterV1Address, QuoterV1, runner);
        this.tokenService = tokenService;
    }
    /**
     * Get quote for swap
     */
    async getSwapQuote(tokenIn, tokenOut, amountIn, feeTier = 500) {
        if (!amountIn) {
            return {
                amountOut: 0n,
                decimals: 0,
                rate: 0,
                gasEstimate: 0n,
            };
        }
        // Check if tokens are native (ETH/AVAX) and replace with wrapped versions for quotation
        const isNativeIn = tokenIn.toLowerCase() === '0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee' ||
            tokenIn.toLowerCase() === '0x0000000000000000000000000000000000000000';
        const isNativeOut = tokenOut.toLowerCase() === '0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee' ||
            tokenOut.toLowerCase() === '0x0000000000000000000000000000000000000000';
        // Get wrapped native token address for quotation if needed
        const wrappedTokens = this.tokenService.getNativeWrappedTokenAddresses();
        const wrappedNative = wrappedTokens[0];
        // Use wrapped versions for quotation
        const actualTokenIn = isNativeIn ? wrappedNative : tokenIn;
        const actualTokenOut = isNativeOut ? wrappedNative : tokenOut;
        // Create path for the swap
        const path = encodePath([actualTokenIn, actualTokenOut], [feeTier]);
        // Get token details (use the original tokens for decimals)
        const inDecimals = await this.tokenService.getTokenDecimals(actualTokenIn);
        const outDecimals = await this.tokenService.getTokenDecimals(actualTokenOut);
        let result = null;
        // Try QuoterV2 first
        try {
            result = await this.getQuoteV2(path, amountIn);
        }
        catch {
            // Fall back to QuoterV1 if V2 fails
            try {
                result = await this.getQuoteV1(path, actualTokenIn, actualTokenOut, amountIn, feeTier);
            }
            catch {
                throw new Error('Could not get quote through any method');
            }
        }
        if (!result || result.amountOut === 0n) {
            throw new Error('Zero output amount in quote');
        }
        const { amountOut, gasEstimate } = result;
        // Calculate rate
        const rate = Number(formatUnits(amountOut, outDecimals)) / Number(formatUnits(amountIn, inDecimals));
        return {
            amountOut,
            decimals: outDecimals,
            rate,
            gasEstimate,
        };
    }
    /**
     * Get quote using QuoterV2
     */
    async getQuoteV2(path, amountIn) {
        if (!this.runner.provider) {
            throw new Error('Provider is not available');
        }
        try {
            // Call quoteExactInput function
            const callData = this.quoterV2.interface.encodeFunctionData('quoteExactInput', [path, amountIn]);
            const response = await this.runner.provider.call({
                to: this.quoterV2.target,
                data: callData,
            });
            if (!response || response === '0x') {
                throw new Error('Empty response from QuoterV2');
            }
            // Try to decode the response using the expected format
            try {
                const decodedResult = this.quoterV2.interface.decodeFunctionResult('quoteExactInput', response);
                // Get the amountOut and gasEstimate
                const amountOut = decodedResult[0];
                // In case gasEstimate is not available (index out of bounds), default to 0n
                const gasEstimate = decodedResult[3] !== undefined ? decodedResult[3] : 0n;
                return {
                    amountOut,
                    gasEstimate,
                };
            }
            catch (decodeError) {
                console.error(`Error decoding result: ${decodeError}`);
                // Try to decode the error message if possible
                try {
                    const errorData = response.startsWith('0x') ? response : `0x${response}`;
                    const decodedError = this.quoterV2.interface.parseError(errorData);
                    console.error(`Contract error: ${decodedError?.name} - ${JSON.stringify(decodedError?.args)}`);
                }
                catch (parseError) {
                    console.error(`Could not parse error: ${parseError}`);
                }
                throw new Error(`Failed to decode QuoterV2 response: ${decodeError}`);
            }
        }
        catch (error) {
            console.error(`QuoterV2 call failed: ${error}`);
            throw new Error(`QuoterV2 quote failed: ${error}`);
        }
    }
    /**
     * Get quote using QuoterV1
     */
    async getQuoteV1(path, tokenIn, tokenOut, amountIn, feeTier) {
        if (!this.runner.provider) {
            throw new Error('Provider is not available');
        }
        // First try the quoteExactInput method
        try {
            const callData = this.quoterV1.interface.encodeFunctionData('quoteExactInput', [path, amountIn]);
            const response = await this.runner.provider.call({
                to: this.quoterV1.target,
                data: callData,
            });
            if (response && response !== '0x') {
                const decodedResult = this.quoterV1.interface.decodeFunctionResult('quoteExactInput', response);
                return {
                    amountOut: decodedResult[0],
                    gasEstimate: 0n,
                };
            }
        }
        catch {
            // Fall back to quoteExactInputSingle if quoteExactInput fails
        }
        // Try exactInputSingle as last resort
        const callData = this.quoterV1.interface.encodeFunctionData('quoteExactInputSingle', [
            tokenIn,
            tokenOut,
            feeTier,
            amountIn,
            0, // No price limit
        ]);
        const response = await this.runner.provider.call({
            to: this.quoterV1.target,
            data: callData,
        });
        if (!response || response === '0x') {
            throw new Error('Empty response from QuoterV1');
        }
        const decodedResult = this.quoterV1.interface.decodeFunctionResult('quoteExactInputSingle', response);
        return {
            amountOut: decodedResult[0],
            gasEstimate: 0n,
        };
    }
}
